import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function toText(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value, null, 2);
}

function useCopyJson(value: unknown) {
  const [copied, setCopied] = useState(false);
  const text = toText(value);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return { copy, copied };
}

interface JsonBlockProps {
  /** The raw value to serialize and copy. Pass a string to use it as-is. */
  value: unknown;
  /**
   * Optional pre-rendered HTML (e.g. syntax-highlighted or linkified).
   * When provided the inner <pre> renders via dangerouslySetInnerHTML while
   * `value` is still used for the clipboard copy.
   */
  html?: string | null;
  className?: string;
}

/**
 * A JSON display block with a copy-to-clipboard button that appears on hover.
 * Drop-in replacement for a styled `<pre>` wrapping `JSON.stringify(...)`:
 *
 *   Before: <pre className="...">{JSON.stringify(data, null, 2)}</pre>
 *   After:  <JsonBlock value={data} className="..." />
 */
export function JsonBlock({ value, html, className }: JsonBlockProps) {
  const { copy, copied } = useCopyJson(value);
  const text = toText(value);

  return (
    <div className={cn("relative group", className)}>
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-1 right-1 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity z-10"
        onClick={copy}
        title="Copy JSON"
        type="button"
      >
        {copied ? (
          <Check className="w-3.5 h-3.5 text-green-500" />
        ) : (
          <Copy className="w-3.5 h-3.5" />
        )}
      </Button>
      {html != null ? (
        // eslint-disable-next-line react/no-danger
        <pre
          className="m-0 p-0 bg-transparent"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <pre className="m-0 p-0 bg-transparent">{text}</pre>
      )}
    </div>
  );
}

interface CopyJsonButtonProps {
  /** The raw value to serialize and copy. Pass a string to use it as-is. */
  value: unknown;
  className?: string;
}

/**
 * A standalone copy-to-clipboard button for JSON values.
 * Use this when you need the button in a header/toolbar rather than as an
 * overlay on the display block (e.g. ProductViewer, JobResultModal).
 */
export function CopyJsonButton({ value, className }: CopyJsonButtonProps) {
  const { copy, copied } = useCopyJson(value);

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn("h-7 w-7 shrink-0", className)}
      onClick={copy}
      title="Copy JSON"
      type="button"
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-green-500" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
    </Button>
  );
}
